import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Mic, Square, Loader2, CheckCircle2, AlertTriangle, RotateCcw } from "lucide-react";
import { API } from "../lib/api";

/** Pagina pubblica (anche iPhone) per registrare note vocali sui clienti.
 *  Raggiungibile via link con token: /rec/:token — nessun login. */
export default function StoreInsightRecorder() {
  const { token } = useParams();
  const [valid, setValid] = useState(null);     // null=loading, true/false
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);   // {transcript, day, time}
  const [error, setError] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/store-insight/rec/${token}`)
      .then((r) => setValid(r.ok))
      .catch(() => setValid(false));
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [token]);

  async function start() {
    setError(""); setResult(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => upload(new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" }));
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (e) {
      setError("Permesso microfono negato o non disponibile su questo dispositivo.");
    }
  }

  function stop() {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    }
  }

  async function upload(blob) {
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      const ext = (blob.type || "").includes("mp4") ? "mp4" : "webm";
      fd.append("audio", blob, `nota.${ext}`);
      const res = await fetch(`${API}/store-insight/rec/${token}/upload`, { method: "POST", body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Errore durante l'invio");
      setResult(data);
    } catch (e) {
      setError(e.message || "Errore durante l'invio della nota");
    } finally {
      setUploading(false);
    }
  }

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  if (valid === null) {
    return <div className="min-h-screen bg-[#141210] flex items-center justify-center text-cream"><Loader2 className="animate-spin" /></div>;
  }
  if (valid === false) {
    return (
      <div className="min-h-screen bg-[#141210] flex flex-col items-center justify-center text-cream px-8 text-center" data-testid="rec-invalid">
        <AlertTriangle className="text-amber-400 mb-4" size={40} />
        <div className="text-lg font-semibold">Link non valido o scaduto</div>
        <div className="text-sm text-cream/60 mt-2">Chiedi all'amministratore un nuovo link di registrazione.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#141210] text-cream flex flex-col" data-testid="store-insight-recorder">
      <div className="px-6 pt-10 pb-6 text-center">
        <div className="text-[11px] tracking-[0.3em] uppercase text-gold">ARCHI—HUB</div>
        <div className="text-[11px] tracking-[0.2em] uppercase text-cream/50 mt-1">Store Insight · Nota vocale</div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6">
        {!result && (
          <>
            <div className="mb-8 font-mono text-5xl tabular-nums text-cream/90" data-testid="rec-timer">{mm}:{ss}</div>
            {!recording ? (
              <button
                onClick={start}
                disabled={uploading}
                className="w-28 h-28 rounded-full bg-gold text-ink flex items-center justify-center shadow-2xl active:scale-95 transition-transform disabled:opacity-50"
                data-testid="rec-start-btn"
              >
                {uploading ? <Loader2 className="animate-spin" size={40} /> : <Mic size={44} />}
              </button>
            ) : (
              <button
                onClick={stop}
                className="w-28 h-28 rounded-full bg-rose-600 text-white flex items-center justify-center shadow-2xl active:scale-95 transition-transform animate-pulse"
                data-testid="rec-stop-btn"
              >
                <Square size={40} fill="currentColor" />
              </button>
            )}
            <div className="mt-8 text-sm text-cream/60 text-center max-w-xs">
              {uploading ? "Trascrizione in corso…" : recording ? "Parla pure: nome cliente e dettagli. Tocca per fermare." : "Tocca il microfono e descrivi il cliente. L'AI estrae i dati automaticamente."}
            </div>
            {error && <div className="mt-6 text-sm text-rose-400 text-center" data-testid="rec-error">{error}</div>}
          </>
        )}

        {result && (
          <div className="w-full max-w-md" data-testid="rec-result">
            <div className="flex flex-col items-center text-center mb-6">
              <CheckCircle2 className="text-emerald-400 mb-3" size={44} />
              <div className="text-lg font-semibold">{result.kind === "visit" ? "Accesso show room registrato" : "Nota salvata"}</div>
              <div className="text-sm text-cream/60 mt-1">{result.day} · {result.time}{result.kind === "visit" ? (result.request_type ? ` — ${result.request_type}` : " — accesso senza nominativo") : " — l'amministratore la confermerà sulla scheda cliente."}</div>
            </div>
            <div className="bg-cream/5 border border-cream/10 rounded-lg p-4 text-sm text-cream/80 leading-relaxed max-h-60 overflow-auto">
              {result.transcript}
            </div>
            <button
              onClick={() => { setResult(null); setElapsed(0); }}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gold text-ink py-3 rounded-lg text-sm tracking-[0.15em] uppercase font-semibold active:scale-[0.98] transition-transform"
              data-testid="rec-again-btn"
            >
              <RotateCcw size={16} /> Registra un'altra nota
            </button>
          </div>
        )}
      </div>
      <div className="pb-8 text-center text-[10px] tracking-[0.2em] uppercase text-cream/30">Funziona anche su iPhone · Safari</div>
    </div>
  );
}
